import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Download,
  FileText,
  Calendar,
  DollarSign,
  Users,
  BarChart3,
  PieChart as PieChartIcon,
  Loader2,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Lock,
  Crown,
  Sparkles,
  CheckCircle,
  Gift,
  Clock
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import jsPDF from "jspdf";

const API = process.env.REACT_APP_BACKEND_URL;

// Chart colors
const CHART_COLORS = ["#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ef4444", "#06b6d4", "#f97316", "#84cc16"];

// Date preset options with access levels
const DATE_PRESETS = [
  { value: "7d", label: "Last 7 Days", level: "basic" },
  { value: "30d", label: "Last 30 Days", level: "basic" },
  { value: "90d", label: "Last 90 Days", level: "full" },
  { value: "ytd", label: "Year to Date", level: "full" },
  { value: "last_year", label: "Last Year", level: "full" },
  { value: "all_time", label: "All Time", level: "full" }
];

// Stat Card Component
const StatCard = ({ title, value, change, prefix = "", suffix = "", icon: Icon, color = "amber" }) => {
  const isPositive = change >= 0;
  const colorClasses = {
    amber: "from-amber-500/20 to-yellow-500/10 border-amber-500/30",
    green: "from-green-500/20 to-emerald-500/10 border-green-500/30",
    blue: "from-blue-500/20 to-cyan-500/10 border-blue-500/30",
    purple: "from-purple-500/20 to-violet-500/10 border-purple-500/30",
    red: "from-red-500/20 to-pink-500/10 border-red-500/30"
  };
  
  return (
    <Card className={`bg-gradient-to-br ${colorClasses[color]} border`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-zinc-400 text-sm">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">
              {prefix}{typeof value === "number" ? value.toLocaleString() : value}{suffix}
            </p>
            {change !== undefined && change !== null && (
              <div className={`flex items-center gap-1 mt-1 text-sm ${isPositive ? "text-green-400" : "text-red-400"}`}>
                {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span>{Math.abs(change)}% vs previous</span>
              </div>
            )}
          </div>
          {Icon && <Icon className={`w-8 h-8 text-${color}-400 opacity-50`} />}
        </div>
      </CardContent>
    </Card>
  );
};

// Upgrade Modal Component
const UpgradeModal = ({ isOpen, onClose, onStartTrial, onUpgrade, accessLevel, trialAvailable }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Crown className="w-6 h-6 text-amber-400" />
            Unlock Full Reports Access
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Get unlimited access to all date ranges, year-over-year comparison, and PDF exports.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Features List */}
          <div className="space-y-3">
            {[
              "All date ranges (7d, 30d, 90d, YTD, Last Year, All Time)",
              "Year-over-year comparison with change %",
              "PDF & CSV export for accounting",
              "Visual charts and analytics",
              "Client retention insights"
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-zinc-300 text-sm">{feature}</span>
              </div>
            ))}
          </div>
          
          {/* Pricing */}
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-lg p-4 border border-amber-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-400 font-semibold">Unlimited Reports Add-On</p>
                <p className="text-zinc-400 text-sm">Full access to all reports</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-white">$10<span className="text-sm text-zinc-400">/mo</span></p>
              </div>
            </div>
          </div>
          
          {/* Alternative: Elite Upgrade */}
          <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-purple-400 font-medium text-sm">Or upgrade to Elite</span>
            </div>
            <p className="text-zinc-400 text-xs">
              Elite membership includes full reports + staff management, booth rent collection, and more for $49.99/mo
            </p>
          </div>
        </div>
        
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {trialAvailable && (
            <Button
              onClick={onStartTrial}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
            >
              <Gift className="w-4 h-4 mr-2" />
              Start 30-Day Free Trial
            </Button>
          )}
          <Button
            onClick={onUpgrade}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
          >
            <Crown className="w-4 h-4 mr-2" />
            Subscribe Now - $10/month
          </Button>
          <Button variant="outline" onClick={onClose} className="w-full border-zinc-700 text-zinc-400">
            Maybe Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Reports Dashboard Component
const ReportsDashboard = ({ 
  token, 
  reportType = "stylist",
  title = "Reports & Analytics"
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("30d");
  const [reportData, setReportData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [accessInfo, setAccessInfo] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchAccessLevel();
  }, [token]);

  useEffect(() => {
    if (accessInfo && accessInfo.access_level !== "none") {
      fetchReportData();
    }
  }, [preset, reportType, accessInfo]);

  const fetchAccessLevel = async () => {
    try {
      const response = await axios.get(`${API}/membership/reports-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccessInfo(response.data);
      
      // Set default preset based on access level
      if (response.data.access_level === "basic") {
        setPreset("30d");
      }
    } catch (error) {
      console.error("Failed to fetch access level:", error);
      setAccessInfo({ access_level: "none" });
    } finally {
      setLoading(false);
    }
  };

  const fetchReportData = async () => {
    if (!accessInfo || accessInfo.access_level === "none") return;
    
    setLoading(true);
    try {
      let endpoint = "";
      
      switch (reportType) {
        case "admin":
          endpoint = "/reports/admin/summary";
          break;
        case "admin_revenue":
          endpoint = "/reports/admin/revenue";
          break;
        case "admin_soldiers":
          endpoint = "/reports/admin/soldiers";
          break;
        case "accountant":
          endpoint = "/reports/accountant/payouts";
          break;
        case "owner":
          endpoint = "/reports/owner/rent-collection";
          break;
        case "owner_renters":
          endpoint = "/reports/owner/renter-performance";
          break;
        case "stylist":
          endpoint = "/reports/stylist/earnings";
          break;
        case "stylist_appointments":
          endpoint = "/reports/stylist/appointments";
          break;
        case "stylist_clients":
          endpoint = "/reports/stylist/clients";
          break;
        case "client":
          endpoint = "/reports/client/spending";
          break;
        case "client_loyalty":
          endpoint = "/reports/client/loyalty";
          break;
        default:
          endpoint = "/reports/stylist/earnings";
      }
      
      const response = await axios.get(`${API}${endpoint}?preset=${preset}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReportData(response.data);
    } catch (error) {
      console.error("Failed to fetch report data:", error);
      toast.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      await axios.post(`${API}/membership/addons/reports/trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Your 30-day free trial has started!");
      setShowUpgradeModal(false);
      fetchAccessLevel();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start trial");
    } finally {
      setStartingTrial(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      const response = await axios.post(`${API}/membership/addons/reports/subscribe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    }
  };

  const exportToCsv = async () => {
    if (!accessInfo?.can_export_csv) {
      setShowUpgradeModal(true);
      return;
    }
    
    setExporting(true);
    try {
      let exportType = "";
      switch (reportType) {
        case "admin":
        case "admin_revenue":
        case "admin_soldiers":
          exportType = "admin_summary";
          break;
        case "accountant":
          exportType = "accountant_payouts";
          break;
        case "owner":
        case "owner_renters":
          exportType = "owner_rent";
          break;
        case "stylist":
        case "stylist_appointments":
        case "stylist_clients":
          exportType = "stylist_earnings";
          break;
        case "client":
        case "client_loyalty":
          exportType = "client_spending";
          break;
        default:
          exportType = "stylist_earnings";
      }
      
      const response = await axios.get(
        `${API}/reports/export/${exportType}?format=csv&preset=${preset}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `report_${exportType}_${preset}_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Report exported successfully!");
    } catch (error) {
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  const exportToPdf = () => {
    if (!accessInfo?.can_export_pdf) {
      setShowUpgradeModal(true);
      return;
    }
    
    if (!reportData) return;
    
    setExporting(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFillColor(24, 24, 27);
      doc.rect(0, 0, pageWidth, 40, "F");
      
      doc.setTextColor(245, 158, 11);
      doc.setFontSize(24);
      doc.text("MyHairChair", 20, 25);
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.text(title, pageWidth - 20, 25, { align: "right" });
      
      // Period
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(10);
      const periodText = reportData.period 
        ? `${new Date(reportData.period.start).toLocaleDateString()} - ${new Date(reportData.period.end).toLocaleDateString()}`
        : preset.toUpperCase();
      doc.text(`Period: ${periodText}`, 20, 50);
      
      // Summary section
      let yPos = 65;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(16);
      doc.text("Summary", 20, yPos);
      
      yPos += 10;
      doc.setFontSize(11);
      
      if (reportData.summary) {
        Object.entries(reportData.summary).forEach(([key, value]) => {
          const label = key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
          const displayValue = typeof value === "number" 
            ? (key.includes("rate") || key.includes("change") ? `${value}%` : key.includes("revenue") || key.includes("earnings") || key.includes("spent") || key.includes("paid") || key.includes("pending") ? `$${value.toLocaleString()}` : value.toLocaleString())
            : value;
          
          doc.text(`${label}: ${displayValue}`, 25, yPos);
          yPos += 7;
          
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
        });
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Generated on ${new Date().toLocaleString()} | Page ${i} of ${pageCount}`,
          pageWidth / 2,
          285,
          { align: "center" }
        );
      }
      
      doc.save(`report_${reportType}_${preset}_${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  // No Access View
  if (accessInfo?.access_level === "none") {
    return (
      <div className="space-y-6">
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
          <CardContent className="py-12 text-center">
            <Lock className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Unlock Reports & Analytics</h2>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              Get insights into your business with detailed reports, charts, and export capabilities.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 max-w-lg mx-auto mb-6">
              <div className="bg-zinc-800/50 rounded-lg p-4 text-left">
                <Badge className="bg-blue-500 text-white mb-2">Pro - $29.99/mo</Badge>
                <p className="text-zinc-400 text-sm">Basic reports (30 days) with CSV export</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4 text-left border border-amber-500/30">
                <Badge className="bg-amber-500 text-black mb-2">Elite - $49.99/mo</Badge>
                <p className="text-zinc-400 text-sm">Full reports + staff management & more</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate("/membership")}
                className="bg-gradient-to-r from-amber-500 to-orange-600"
              >
                <Crown className="w-4 h-4 mr-2" />
                Start 30-Day Free Trial
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && !reportData) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  // Get available presets based on access level
  const availablePresets = DATE_PRESETS.filter(p => 
    accessInfo?.allowed_presets?.includes(p.value)
  );

  return (
    <div className="space-y-6" ref={reportRef}>
      {/* Trial Banner */}
      {accessInfo?.reason === "reports_addon_trial" && accessInfo?.trial_expires && (
        <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30">
          <CardContent className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-400" />
              <span className="text-green-400">
                <strong>Free Trial Active</strong> - Expires {new Date(accessInfo.trial_expires).toLocaleDateString()}
              </span>
            </div>
            <Button
              size="sm"
              onClick={handleUpgrade}
              className="bg-green-500 hover:bg-green-600 text-black"
            >
              Subscribe Now
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Basic Access Upgrade Banner */}
      {accessInfo?.access_level === "basic" && (
        <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
          <CardContent className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 text-sm">
                <strong>Basic Reports</strong> - Upgrade for extended date ranges, YoY comparison & PDF export
              </span>
            </div>
            <Button
              size="sm"
              onClick={() => setShowUpgradeModal(true)}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              <Crown className="w-4 h-4 mr-1" />
              Upgrade
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-amber-400" />
            {title}
          </h2>
          {reportData?.comparison && (
            <p className="text-zinc-400 text-sm mt-1">
              Comparing: {reportData.comparison.current || reportData.comparison.current_period}
            </p>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700 text-white">
              <Calendar className="w-4 h-4 mr-2 text-zinc-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {DATE_PRESETS.map(p => {
                const isLocked = !accessInfo?.allowed_presets?.includes(p.value);
                return (
                  <SelectItem 
                    key={p.value} 
                    value={p.value} 
                    className="text-white hover:bg-zinc-700"
                    disabled={isLocked}
                  >
                    <div className="flex items-center gap-2">
                      {isLocked && <Lock className="w-3 h-3 text-zinc-500" />}
                      {p.label}
                      {isLocked && <Badge className="text-xs bg-amber-500/20 text-amber-400 ml-1">PRO</Badge>}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            onClick={fetchReportData}
            className="border-zinc-700 text-zinc-300"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            onClick={exportToCsv}
            disabled={exporting}
            className="border-zinc-700 text-zinc-300"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            CSV
          </Button>
          
          <Button
            onClick={exportToPdf}
            disabled={exporting || !accessInfo?.can_export_pdf}
            className={accessInfo?.can_export_pdf 
              ? "bg-gradient-to-r from-amber-500 to-orange-600" 
              : "bg-zinc-700 text-zinc-400"
            }
          >
            {!accessInfo?.can_export_pdf && <Lock className="w-4 h-4 mr-2" />}
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {reportData?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {renderSummaryCards(reportData.summary, reportType)}
        </div>
      )}

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Main Chart */}
        {reportData?.chart_data && reportData.chart_data.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800 md:col-span-2">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-400" />
                Trend Over Time
                {!accessInfo?.has_yoy_comparison && (
                  <Badge className="ml-2 bg-amber-500/20 text-amber-400 text-xs">
                    <Lock className="w-3 h-3 mr-1" />
                    YoY Comparison - Upgrade
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  {renderMainChart(reportData.chart_data, reportType)}
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Service Breakdown Pie Chart */}
        {reportData?.service_breakdown && reportData.service_breakdown.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <PieChartIcon className="w-5 h-5 text-purple-400" />
                Service Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={reportData.service_breakdown.slice(0, 6)}
                      dataKey="revenue"
                      nameKey="service"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name.substring(0, 10)} ${(percent * 100).toFixed(0)}%`}
                    >
                      {reportData.service_breakdown.slice(0, 6).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46" }}
                      formatter={(value) => [`$${value}`, "Revenue"]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Day Distribution Bar Chart */}
        {reportData?.day_distribution && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-400" />
                Day Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reportData.day_distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                    <XAxis dataKey="day" stroke="#a1a1aa" />
                    <YAxis stroke="#a1a1aa" />
                    <Tooltip contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46" }} />
                    <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Client Segments */}
        {reportData?.client_segments && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-green-400" />
                Client Segments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={reportData.client_segments}
                      dataKey="count"
                      nameKey="segment"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ segment, count }) => `${segment}: ${count}`}
                    >
                      {reportData.client_segments.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Commission Breakdown */}
        {reportData?.commission_breakdown && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                Commission Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(reportData.commission_breakdown).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                    <span className="text-zinc-300 capitalize">{key.replace(/_/g, " ")}</span>
                    <span className="text-xl font-bold text-green-400">${value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Breakdown for Owner */}
        {reportData?.breakdown && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-400" />
                Payment Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                  <span className="text-green-400">Paid</span>
                  <Badge className="bg-green-500 text-white">{reportData.breakdown.paid_count}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                  <span className="text-amber-400">Pending</span>
                  <Badge className="bg-amber-500 text-black">{reportData.breakdown.pending_count}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                  <span className="text-red-400">Overdue</span>
                  <Badge className="bg-red-500 text-white">{reportData.breakdown.overdue_count}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top Clients Table */}
        {reportData?.top_clients && reportData.top_clients.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800 md:col-span-2">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-400" />
                Top Clients
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left text-zinc-400 text-sm py-2 px-3">Client</th>
                      <th className="text-center text-zinc-400 text-sm py-2 px-3">Visits</th>
                      <th className="text-right text-zinc-400 text-sm py-2 px-3">Total Spent</th>
                      <th className="text-right text-zinc-400 text-sm py-2 px-3">Last Visit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.top_clients.map((client, index) => (
                      <tr key={client.client_id} className="border-b border-zinc-800/50">
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-400 font-bold text-sm">
                              {index + 1}
                            </div>
                            <span className="text-white">{client.name}</span>
                          </div>
                        </td>
                        <td className="text-center text-zinc-300 py-3 px-3">{client.visits}</td>
                        <td className="text-right text-green-400 font-medium py-3 px-3">
                          ${client.total_spent.toLocaleString()}
                        </td>
                        <td className="text-right text-zinc-400 py-3 px-3">{client.last_visit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Renters Table */}
        {reportData?.renters && reportData.renters.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800 md:col-span-2">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                Renter Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left text-zinc-400 text-sm py-2 px-3">Renter</th>
                      <th className="text-center text-zinc-400 text-sm py-2 px-3">Booth</th>
                      <th className="text-right text-zinc-400 text-sm py-2 px-3">Rent</th>
                      <th className="text-right text-zinc-400 text-sm py-2 px-3">Total Paid</th>
                      <th className="text-center text-zinc-400 text-sm py-2 px-3">On-Time</th>
                      <th className="text-center text-zinc-400 text-sm py-2 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.renters.map((renter) => (
                      <tr key={renter.renter_id} className="border-b border-zinc-800/50">
                        <td className="py-3 px-3 text-white">{renter.name}</td>
                        <td className="text-center text-zinc-300 py-3 px-3">{renter.booth}</td>
                        <td className="text-right text-zinc-300 py-3 px-3">${renter.rent_amount}</td>
                        <td className="text-right text-green-400 font-medium py-3 px-3">
                          ${renter.total_paid.toLocaleString()}
                        </td>
                        <td className="text-center py-3 px-3">
                          <Badge className={renter.on_time_rate >= 90 ? "bg-green-500" : renter.on_time_rate >= 70 ? "bg-amber-500" : "bg-red-500"}>
                            {renter.on_time_rate}%
                          </Badge>
                        </td>
                        <td className="text-center py-3 px-3">
                          <Badge className={renter.status === "active" ? "bg-green-500/20 text-green-400" : "bg-zinc-500/20 text-zinc-400"}>
                            {renter.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onStartTrial={handleStartTrial}
        onUpgrade={handleUpgrade}
        accessLevel={accessInfo?.access_level}
        trialAvailable={!accessInfo?.trial_used}
      />
    </div>
  );
};

// Helper functions for rendering
function renderSummaryCards(summary, reportType) {
  const cards = [];
  
  if (reportType.startsWith("admin")) {
    if (summary.new_users !== undefined) cards.push(<StatCard key="users" title="New Users" value={summary.new_users} change={summary.new_users_change} icon={Users} color="blue" />);
    if (summary.appointments !== undefined) cards.push(<StatCard key="appts" title="Appointments" value={summary.appointments} change={summary.appointments_change} icon={Calendar} color="purple" />);
    if (summary.revenue !== undefined) cards.push(<StatCard key="revenue" title="Revenue" value={summary.revenue} prefix="$" change={summary.revenue_change} icon={DollarSign} color="green" />);
    if (summary.total_users !== undefined) cards.push(<StatCard key="total" title="Total Users" value={summary.total_users} icon={Users} color="amber" />);
  } else if (reportType === "accountant") {
    cards.push(<StatCard key="paid" title="Total Paid" value={summary.total_paid} prefix="$" change={summary.total_paid_change} icon={DollarSign} color="green" />);
    cards.push(<StatCard key="count" title="Payout Count" value={summary.payout_count} icon={FileText} color="blue" />);
    cards.push(<StatCard key="pending" title="Pending" value={summary.pending_payouts} prefix="$" icon={DollarSign} color="amber" />);
    cards.push(<StatCard key="pending_count" title="Pending Count" value={summary.pending_count} icon={FileText} color="purple" />);
  } else if (reportType.startsWith("owner")) {
    if (summary.total_collected !== undefined) cards.push(<StatCard key="collected" title="Collected" value={summary.total_collected} prefix="$" change={summary.collected_change} icon={DollarSign} color="green" />);
    if (summary.total_pending !== undefined) cards.push(<StatCard key="pending" title="Pending" value={summary.total_pending} prefix="$" icon={DollarSign} color="amber" />);
    if (summary.collection_rate !== undefined) cards.push(<StatCard key="rate" title="Collection Rate" value={summary.collection_rate} suffix="%" icon={TrendingUp} color="blue" />);
    if (summary.active_renters !== undefined) cards.push(<StatCard key="renters" title="Renters" value={summary.active_renters} icon={Users} color="purple" />);
  } else if (reportType.startsWith("stylist")) {
    if (summary.gross_earnings !== undefined) cards.push(<StatCard key="gross" title="Gross Earnings" value={summary.gross_earnings} prefix="$" change={summary.earnings_change} icon={DollarSign} color="green" />);
    if (summary.net_earnings !== undefined) cards.push(<StatCard key="net" title="Net Earnings" value={summary.net_earnings} prefix="$" icon={DollarSign} color="amber" />);
    if (summary.total_appointments !== undefined) cards.push(<StatCard key="appts" title="Appointments" value={summary.total_appointments} icon={Calendar} color="blue" />);
    if (summary.completion_rate !== undefined) cards.push(<StatCard key="rate" title="Completion" value={summary.completion_rate} suffix="%" icon={TrendingUp} color="purple" />);
    if (summary.retention_rate !== undefined) cards.push(<StatCard key="retention" title="Retention" value={summary.retention_rate} suffix="%" icon={Users} color="green" />);
    if (summary.total_clients !== undefined) cards.push(<StatCard key="clients" title="Total Clients" value={summary.total_clients} icon={Users} color="amber" />);
  } else if (reportType.startsWith("client")) {
    if (summary.total_spent !== undefined) cards.push(<StatCard key="spent" title="Total Spent" value={summary.total_spent} prefix="$" change={summary.spending_change} icon={DollarSign} color="amber" />);
    if (summary.total_refunded !== undefined) cards.push(<StatCard key="refunded" title="Refunded" value={summary.total_refunded} prefix="$" icon={DollarSign} color="red" />);
    if (summary.total_appointments !== undefined) cards.push(<StatCard key="appts" title="Appointments" value={summary.total_appointments} icon={Calendar} color="blue" />);
    if (summary.current_points !== undefined) cards.push(<StatCard key="points" title="Points" value={summary.current_points} icon={TrendingUp} color="purple" />);
  }
  
  return cards;
}

function renderMainChart(chartData, reportType) {
  if (!chartData || chartData.length === 0) return null;
  
  const keys = Object.keys(chartData[0]).filter(k => !["date", "month", "week", "label"].includes(k));
  
  if (reportType === "accountant" || reportType.includes("payout")) {
    return (
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
        <XAxis dataKey="week" stroke="#a1a1aa" tick={{ fontSize: 12 }} />
        <YAxis stroke="#a1a1aa" tick={{ fontSize: 12 }} />
        <Tooltip contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46", borderRadius: "8px" }} formatter={(value) => [`$${value}`, ""]} />
        <Legend />
        <Bar dataKey="amount" fill="#10b981" name="Amount" radius={[4, 4, 0, 0]} />
        <Bar dataKey="count" fill="#f59e0b" name="Count" radius={[4, 4, 0, 0]} />
      </BarChart>
    );
  }
  
  if (reportType.includes("earning") || reportType.includes("revenue") || reportType.includes("spending")) {
    return (
      <AreaChart data={chartData}>
        <defs>
          <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
        <XAxis dataKey={chartData[0].date ? "date" : "month"} stroke="#a1a1aa" tick={{ fontSize: 12 }} />
        <YAxis stroke="#a1a1aa" tick={{ fontSize: 12 }} />
        <Tooltip contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46", borderRadius: "8px" }} formatter={(value, name) => [`$${value}`, name]} />
        <Legend />
        <Area type="monotone" dataKey="earnings" stroke="#10b981" fillOpacity={1} fill="url(#colorEarnings)" name="Earnings" />
        {chartData[0].tips !== undefined && <Area type="monotone" dataKey="tips" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.2} name="Tips" />}
      </AreaChart>
    );
  }
  
  return (
    <LineChart data={chartData}>
      <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
      <XAxis dataKey={chartData[0].date ? "date" : chartData[0].label ? "label" : "month"} stroke="#a1a1aa" tick={{ fontSize: 12 }} />
      <YAxis stroke="#a1a1aa" tick={{ fontSize: 12 }} />
      <Tooltip contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46", borderRadius: "8px" }} />
      <Legend />
      {keys.map((key, index) => (
        <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[index % CHART_COLORS.length]} strokeWidth={2} dot={{ fill: CHART_COLORS[index % CHART_COLORS.length] }} name={key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())} />
      ))}
    </LineChart>
  );
}

export default ReportsDashboard;
